import { Component, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';

import { PortfolioApi } from '../../core/api/portfolio-api';
import { PortfolioState } from '../../core/portfolio-state/portfolio-state';
import { UserPortfolio } from '../../core/api/models';

@Component({
  selector: 'app-manage-portfolios-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatDialogModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatListModule,
  ],
  template: `
    <h2 mat-dialog-title>Manage Portfolios</h2>

    <mat-dialog-content class="manage-portfolios-content">
      <div class="create-portfolio-form">
        <mat-form-field appearance="outline" class="full-width">
          <mat-label>New Portfolio Name</mat-label>
          <input
            matInput
            [(ngModel)]="newPortfolioName"
            placeholder="e.g. Retirement, Crypto, Family"
            (keyup.enter)="createPortfolio()"
          />
        </mat-form-field>
        <button
          mat-flat-button
          color="primary"
          [disabled]="!newPortfolioName().trim() || isSubmitting()"
          (click)="createPortfolio()"
        >
          Add
        </button>
      </div>

      @if (error()) {
        <p class="error-msg">{{ error() }}</p>
      }

      <div class="portfolio-list">
        <h3>Your Portfolios</h3>
        @for (p of state.portfolios(); track p.id) {
          <div class="portfolio-item">
            @if (editingId() === p.id) {
              <mat-form-field appearance="outline" class="inline-input">
                <input matInput [(ngModel)]="editName" (keyup.enter)="saveRename(p.id)" />
              </mat-form-field>
              <button mat-icon-button color="primary" (click)="saveRename(p.id)">
                <mat-icon>check</mat-icon>
              </button>
              <button mat-icon-button (click)="cancelRename()">
                <mat-icon>close</mat-icon>
              </button>
            } @else {
              <div class="portfolio-info">
                <span class="portfolio-name">{{ p.name }}</span>
                @if (p.isDefault) {
                  <span class="default-badge">Default</span>
                }
              </div>
              <div class="portfolio-actions">
                <button mat-icon-button title="Rename portfolio" (click)="startRename(p)">
                  <mat-icon>edit</mat-icon>
                </button>
                @if (!p.isDefault && state.portfolios().length > 1) {
                  <button
                    mat-icon-button
                    color="warn"
                    title="Delete portfolio"
                    (click)="deletePortfolio(p.id)"
                  >
                    <mat-icon>delete</mat-icon>
                  </button>
                }
              </div>
            }
          </div>
        }
      </div>
    </mat-dialog-content>

    <mat-dialog-actions align="end">
      <button mat-button (click)="dialogRef.close()">Close</button>
    </mat-dialog-actions>
  `,
  styles: [
    `
      .manage-portfolios-content {
        min-width: 360px;
        max-width: 500px;
      }
      .create-portfolio-form {
        display: flex;
        gap: 12px;
        align-items: center;
        margin-bottom: 16px;
      }
      .full-width {
        flex: 1;
      }
      .portfolio-list {
        margin-top: 16px;
      }
      .portfolio-item {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 8px 12px;
        border-radius: 6px;
        background: var(--surface-overlay, rgba(255, 255, 255, 0.04));
        margin-bottom: 8px;
      }
      .portfolio-info {
        display: flex;
        align-items: center;
        gap: 8px;
      }
      .portfolio-name {
        font-weight: 500;
      }
      .default-badge {
        font-size: 0.75rem;
        padding: 2px 6px;
        border-radius: 4px;
        background: var(--mat-sys-primary-container, #e0e0e0);
        color: var(--mat-sys-on-primary-container, #333);
      }
      .portfolio-actions {
        display: flex;
        gap: 4px;
      }
      .inline-input {
        flex: 1;
        margin-bottom: -1.25em;
      }
      .error-msg {
        color: var(--mat-sys-error, #f44336);
        font-size: 0.875rem;
        margin-bottom: 8px;
      }
    `,
  ],
})
export class ManagePortfoliosDialog {
  readonly dialogRef = inject(MatDialogRef<ManagePortfoliosDialog>);
  readonly state = inject(PortfolioState);
  private readonly api = inject(PortfolioApi);

  readonly newPortfolioName = signal('');
  readonly isSubmitting = signal(false);
  readonly editingId = signal<string | null>(null);
  readonly editName = signal('');
  readonly error = signal<string | null>(null);

  createPortfolio(): void {
    const name = this.newPortfolioName().trim();
    if (!name || this.isSubmitting()) return;

    this.isSubmitting.set(true);
    this.error.set(null);

    this.api.createPortfolio({ name }).subscribe({
      next: () => {
        this.newPortfolioName.set('');
        this.isSubmitting.set(false);
        this.state.loadPortfolios();
      },
      error: (err) => {
        this.isSubmitting.set(false);
        this.error.set(err.error?.detail || 'Failed to create portfolio.');
      },
    });
  }

  startRename(portfolio: UserPortfolio): void {
    this.editingId.set(portfolio.id);
    this.editName.set(portfolio.name);
  }

  cancelRename(): void {
    this.editingId.set(null);
    this.editName.set('');
  }

  saveRename(id: string): void {
    const name = this.editName().trim();
    if (!name) return;

    this.api.updatePortfolio(id, { name }).subscribe({
      next: () => {
        this.cancelRename();
        this.state.loadPortfolios();
      },
      error: (err) => {
        this.error.set(err.error?.detail || 'Failed to rename portfolio.');
      },
    });
  }

  deletePortfolio(id: string): void {
    this.error.set(null);
    this.api.deletePortfolio(id).subscribe({
      next: () => {
        if (this.state.selectedPortfolioId() === id) {
          this.state.selectPortfolio('ALL');
        }
        this.state.loadPortfolios();
      },
      error: (err) => {
        this.error.set(err.error?.detail || 'Failed to delete portfolio.');
      },
    });
  }
}
